// @flow

import React, {PureComponent, Fragment} from 'react';
import classNames from 'classnames';
import flattenDeep from 'lodash.flattendeep';

import {ScrollableArea, Left, Right} from '@pb/kit-component-scrollable-area';
import {uniqId} from '@pb/kit-util-uniq-id';
import {convertPxToRem} from '@pb/kit-util-convert-px-to-rem';
import {hashedClasses} from '@pb/kit-util-with-css-module';

import {
    DataTableDragEventsContext,
    ALLOWED_DRAG_AND_DROP_AREA_DATA_ID
} from './contexts/data-table-drag-events-context';
import {DataTableHover} from './data-table-hover';
import {Table} from './table.component';
import {DataTableResizeClass} from './data-table-resize-class';
import {Row} from './data-table-row';
import {Cell} from './data-table-cell';
import {Navigation} from './data-table-navigation';
import {PinnedRowsHeader} from './headers/data-table-pinned-rows-header';
import {MainHeader} from './headers/data-table-main-header';
import {DataTableReordering} from './data-table-reordering';
import {THead} from './data-table-thead';
import {TBody} from './data-table-tbody';
import {DataTableRowPlaceholder} from './data-table-row-placeholder';
import {
    TableSize,
    DefaultVirtualizeHorizontalData,
    CLASS_PROPERTIES,
    TIMER_DELAY,
    tableHeaderModificators,
    SCROLL_DIRECTION,
    TABLE_PARTS,
    DataTableRowSize,
    HighlightType,
    treeRowsBodyName
} from './constants';
import {DataTableDefaultNavigation} from './data-table-default-navigation';
import {VirtualizedCellsArea} from './virtualized-cells-area';
import {
    prepareTreeRowsSections,
    splitByPinnedRows,
    splitByTopTableRows,
    getColumnsWidthData,
    getRowEndIndex,
    getRowStartIndex,
    getPropertyClasses,
    createRowsMap,
    isLastChild,
    isRowCollapsed,
    generateRowId,
    prepareTreeObj,
    getRowHover
} from './utils';
import './data-table.styl';
import styles from './data-table-scroll.module.styl';

import type {Element} from 'react';
import type {DataTableDragEventsContextType} from './contexts/data-table-drag-events-context';
import type {
    Column,
    DataTableResizeState,
    RowData,
    TreeRowData,
    RenderHeaderType,
    RenderTableType,
    VirtualizeHorizontalData,
    PropertyClasses,
    RowsMapType,
    ColumnGroup,
    TableRowsProps,
    VirtualizedRow,
    RenderRowsParams,
    RenderTreeRowsParams,
    RenderTreeRowsStructureParams,
    RowPlaceholderParams,
    DataTableProps,
    DefaultProps,
    RenderRows,
    RenderRowsTree,
    RenderHeaders
} from './typedef';


const hashedClassnames = hashedClasses(styles);

function *indexGenerator(length: number): Generator<number, void, void> {
    let index = 0;
    while (index < length) {
        yield index++;
    }
}

type Props<DataRow = {||}, DataColumn = {||}> = {|
    onRef?: (ref: HTMLElement | null) => void,
    ...DataTableProps<DataRow, DataColumn>,
    ...DefaultProps<DataColumn>,
|};

type PropertyElement = {|
    ...PropertyClasses,
    index: number,
|};

export type OtherColumnsWidthProps = {|
    width: number,
    widthArray: Array<number>,
    offsetWidth: Array<number>,
|};

type State<DataColumn> = {|
    rowStartIndex: number,
    rowsEndIndex: number,
    topOffset: number,
    preparedColumns: ColumnGroup<Array<Column<DataColumn>>>,
    otherColumnsWidth: OtherColumnsWidthProps,
    fixedColumnsWidth: number,
|};

const MIN_CELLS_FOR_VIRT = 30;
const OVERSCAN_CELLS_RATIO = 1.05;
const MIN_ROWS_FOR_VIRT = 50;

// eslint-disable-next-line  @stylistic/js/max-len
export class DataTable<DataRow = {||}, DataColumn = {||}> extends PureComponent<Props<DataRow, DataColumn>, State<DataColumn>> {
    dataTable: ?HTMLElement;
    id: string;
    fixedClass: string;
    hover: DataTableHover;
    resize: DataTableResizeClass;
    externalScrollHandler: (delta: number) => void;
    scroll: any;
    timer: TimeoutID;
    rowsMap: RowsMapType;
    previousTopOffset: number = 0;
    rowsVirtualization: boolean = false;
    preparedTreesSection: Array<Array<TreeRowData<Object>>> = [];
    flatTreeRows: Array<TreeRowData<Object>> = [];

    context: DataTableDragEventsContextType;
    static contextType: React$Context<DataTableDragEventsContextType> = DataTableDragEventsContext;

    static defaultProps = {
        id: uniqId(),
        mix: '',
        modal: false,
        onStopResize: () => undefined,
        tableSize: TableSize.DEFAULT,
        showNoDataMsg: false,
        overscanRows: 10,
        overscanCellsRatio: OVERSCAN_CELLS_RATIO,
        viewportHeight: window.innerHeight,
        rowsVirtualization: false,
        cellsVirtualization: false,
        minCellsForVirtualization: MIN_CELLS_FOR_VIRT,
        minRowsForVirtualization: MIN_ROWS_FOR_VIRT,
        scrollToCell: () => undefined
    };

    constructor(props: Props<DataRow, DataColumn>) {
        super(props);

        this.id = props.id;
        this.fixedClass = CLASS_PROPERTIES.fixed;
        this.externalScrollHandler = () => undefined;
        this.init();

        const {viewportHeight, overscanRows} = props;
        const {columns = [], rows = [], treeRows = []} = props.data;
        const preparedColumnsData = this.getPreparedColumnsData(columns);

        this.prepareRowsForVirtualization(rows, treeRows);

        const rowsEndIndex = getRowEndIndex({
            viewportHeight,
            scrollOffset: 0,
            overscanRows,
            rowsCount: this.rowsMap.rowsHeight.length,
            rowsOffset: this.rowsMap.rowsOffset,
            direction: SCROLL_DIRECTION.down
        });

        this.state = {
            ...preparedColumnsData,
            rowStartIndex: 0,
            rowsEndIndex,
            topOffset: 0
        };
    }

    componentDidMount() {
        const {wrapperElement} = this.props;

        if (!wrapperElement) {
            window.addEventListener('resize', this.observedResize);
        }

        if (this.props.getRefTable && this.dataTable) {
            this.props.getRefTable(this.dataTable);
        }

        if (wrapperElement) {
            wrapperElement.addEventListener('resize', this.observedResize);
            wrapperElement.addEventListener('scroll', this.onScroll);
        } else {
            document.addEventListener('scroll', this.onScroll);
        }
        this.scrollToCell();
    }

    componentDidUpdate(prevProps: Props<DataRow, DataColumn>) {
        if (this.props.id !== prevProps.id) {
            this.id = this.props.id;
            this.init();
        }

        if (this.props.data !== prevProps.data) {
            const {rows = [], treeRows = []} = this.props.data;
            this.prepareRowsForVirtualization(rows, treeRows);

            this.setState({
                ...this.getPreparedColumnsData(this.props.data.columns)
            }, () => this.getVisibleRowRange(this.getCurrentScrollOffset()));
        }
    }

    componentWillUnmount() {
        const {wrapperElement} = this.props;
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.observedResize);

        if (wrapperElement) {
            wrapperElement.removeEventListener('scroll', this.onScroll);
            wrapperElement.removeEventListener('resize', this.observedResize);
        }
    }

    prepareRowsForVirtualization = <T>(rows: Array<RowData<T>>, treeRows: Array<TreeRowData<T>>): void => {
        /*
         * @note: If we have some rows with NON STANDARD height, we have to assign it to `rowHeight` prop inside
         * controller rowRender method.
         * After that the `rowsMap` object with all necessary data will be created
         */

        this.preparedTreesSection = prepareTreeRowsSections(treeRows);
        this.flatTreeRows = flattenDeep(this.preparedTreesSection);

        const availableRows = this.getAvailableRows(rows, this.flatTreeRows);
        this.rowsVirtualization = this.isRowVirtualizationEnable(availableRows.length);

        this.rowsMap = createRowsMap(availableRows, DataTableRowSize[this.props.tableSize || TableSize.DEFAULT]);
    };

    scrollToCell = () => {
        if (this.dataTable && this.props.scrollToCell) {
            const {offsetWidth, width} = this.state.otherColumnsWidth;
            this.props.scrollToCell(offsetWidth, width, this.dataTable.offsetWidth);
        }
    };

    init() {
        this.hover = new DataTableHover(this.id);
        this.resize = new DataTableResizeClass(this.id, getPropertyClasses);

        if (this.props.getHoverInstance) {
            this.props.getHoverInstance(this.hover);
        }
    }

    getAvailableRows = <T>(rows: Array<RowData<T>>, treeRows: Array<TreeRowData<T>>) => {
        return rows.length > 0 ? rows : treeRows;
    };

    observedResize = (): void => {
        if (this.rowsVirtualization) {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.getVisibleRowRange(this.state.topOffset), TIMER_DELAY);
        }
    };

    getScrollDirection = (offset: number) => {
        return offset > this.previousTopOffset ? SCROLL_DIRECTION.down : SCROLL_DIRECTION.up;
    };

    getTableTopOffset = () => {
        if (this.dataTable) {
            const tableBounding = this.dataTable.getBoundingClientRect();
            const tableTop = Math.round(tableBounding.top);
            return tableTop < 0 ? Math.abs(tableTop) : 0;
        }
        return 0;
    };

    getCurrentScrollOffset = () => {
        const {wrapperElement, tableSize} = this.props;
        const buffer = DataTableRowSize[tableSize || TableSize.DEFAULT];

        const offset = wrapperElement
            ? wrapperElement.scrollTop - (this.dataTable?.offsetTop || 0)
            : this.getTableTopOffset();

        return Math.max(0, offset - buffer);
    };

    onScroll = (): void => {
        if (this.dataTable) {
            const topOffset = this.getCurrentScrollOffset();

            if (Math.abs(this.previousTopOffset - topOffset) >= DataTableRowSize[TableSize.DEFAULT]) {
                this.getVisibleRowRange(topOffset, this.getScrollDirection(topOffset));
            }
        }
    };

    isRowVirtualizationEnable = (rowsCount: number): boolean => {
        return !!this.props.rowsVirtualization && (rowsCount >= Number(this.props.minRowsForVirtualization));
    };

    getPreparedColumnsData = <T>(columns: Array<Column<T>>): {|
        preparedColumns: ColumnGroup<Array<Column<T>>>,
        otherColumnsWidth: OtherColumnsWidthProps,
        fixedColumnsWidth: number,
    |} => {
        const preparedColumns = this.prepareColumns(columns);
        const fixedColumnsWidth = getColumnsWidthData(preparedColumns.fixed).width;
        const otherColumnsWidth = getColumnsWidthData(preparedColumns.others);

        return {preparedColumns, fixedColumnsWidth, otherColumnsWidth};
    };

    prepareColumns = <T>(columns: Array<Column<T>>): ColumnGroup<Array<Column<T>>> => {
        return columns.reduce((acc, i) => {
            if (i.fixed) {
                acc.fixed.push(i);
            } else {
                acc.others.push(i);
            }
            return acc;
        }, {fixed: [], others: []});
    };

    isCellVirtualizationEnable = (cellsCount: number) => (
        !!this.props.cellsVirtualization && cellsCount >= Number(this.props.minCellsForVirtualization)
    );

    splitRows = <T>(rows: Array<RowData<T>>): {|
        topTableRows: Array<RowData<T>>,
        ordinaryRows: Array<RowData<T>>,
        pinnedRows: Array<RowData<T>>,
    |} => {
        const {topTableRows = [], ordinaryRows: nonTopTableRows} = splitByTopTableRows<T>(rows);
        const {pinnedRows = [], ordinaryRows = []} = splitByPinnedRows<T>(nonTopTableRows);

        return {topTableRows, ordinaryRows, pinnedRows};
    }

    getVisibleRowRange = (
        scrollOffsetTop: number, direction: $Values<typeof SCROLL_DIRECTION> = SCROLL_DIRECTION.down
    ) => {
        const {overscanRows, data: {rows = []}, wrapperElement} = (this.props: Props<DataRow, DataColumn>);
        const availableRows = this.getAvailableRows(rows, this.flatTreeRows);

        if (this.rowsVirtualization) {
            const rowStartIndex = getRowStartIndex({
                scrollOffsetTop,
                direction,
                overscanRows,
                rowsOffset: this.rowsMap.rowsOffset
            });

            const topOffset = this.rowsMap.rowsOffset[rowStartIndex];

            const rowsEndIndex = getRowEndIndex({
                scrollOffset: scrollOffsetTop,
                viewportHeight: wrapperElement ? wrapperElement.clientHeight : window.innerHeight,
                overscanRows,
                rowsCount: availableRows.length,
                rowsOffset: this.rowsMap.rowsOffset,
                direction
            });

            this.setState(state => {
                if (state.rowsEndIndex !== rowsEndIndex) {
                    return {topOffset, rowsEndIndex, rowStartIndex};
                }

                return null;
            });
        } else {
            this.setState({topOffset: 0, rowsEndIndex: 0, rowStartIndex: 0});
        }
        this.previousTopOffset = scrollOffsetTop;
    };

    getVirtualizedCells = <T>(
        data: Array<Column<T>>,
        virtualizeHorizontalData: VirtualizeHorizontalData
    ): Array<Column<T>> => {
        const {cellStartIndex, cellEndIndex, isVirtualizedCells} = virtualizeHorizontalData;
        return isVirtualizedCells ? data.slice(cellStartIndex, cellEndIndex) : data;
    };

    getReorderingRow = (
        content: Element<typeof Cell> | Array<Element<typeof Cell>>,
        virtualizeHorizontalData: VirtualizeHorizontalData
    ) => {
        const {reordering} = this.props;

        if (reordering) {
            return (
                <DataTableReordering
                    tableId={this.props.id}
                    scroll={{getScroll: this.getScroll, getScrollHandler: this.getScrollHandler}}
                    virtualizeHorizontalData={virtualizeHorizontalData}
                    {...reordering}>
                    {content}
                </DataTableReordering>
            );
        }
        return <Row virtualizeHorizontalData={virtualizeHorizontalData}>{content}</Row>;
    };

    renderHeaders = <T>(
        {
            columns,
            width,
            fixed = false,
            virtualizeHorizontalData
        }: RenderHeaderType<T>,
        position: $Values<typeof TABLE_PARTS> = TABLE_PARTS.left,
        stickyScroll: boolean = false
    ) => {
        const {cellStartIndex, isVirtualizedCells} = virtualizeHorizontalData;
        const headers = ((this.props.headers: any): RenderHeaders<T>);
        const {data: {rows = []}} = this.props;
        const {pinnedRows} = this.splitRows(rows);

        const content = this.getVirtualizedCells<T>(columns, virtualizeHorizontalData)
            .map((column, key, array) => {
                const uniqueIndex = isVirtualizedCells ? cellStartIndex + key : key;

                const {
                    resizable,
                    onResize,
                    onStartResize,
                    onStopResize
                } = this.getResizeHandlers({columns, uniqueIndex});

                const cellOptions = {
                    id: `${this.id}${getPropertyClasses({fixed})}-header-${uniqueIndex}`,
                    key: column.columnId,
                    headerCell: true,
                    index: uniqueIndex,
                    isLastChild: isLastChild(array, key),
                    resizable,
                    onResize,
                    onStartResize,
                    onStopResize,
                    tableId: `${this.id}`
                };
                return headers({column}, cellOptions);
            });

        return (
            <MainHeader
                className={position === TABLE_PARTS.left
                    ? tableHeaderModificators.leftHeaders
                    : tableHeaderModificators.rightHeaders}
                getHeaderTopOffset={this.props.getHeaderTopOffset}
                stickyReferenceElement={this.props.stickyReferenceElement}
                containerEventListenersType={this.props.containerEventListenersType}
                modal={this.props.modal}
                disabledScroll={this.props.disabledScroll}
                hasPinnedRows={!!pinnedRows.length}
                stickyScroll={stickyScroll}>
                <Table width={width} id={`${this.id}${getPropertyClasses({fixed})}-header-table`}>
                    {fixed ? (
                        <Row
                            fixed={fixed}
                            virtualizeHorizontalData={virtualizeHorizontalData}>
                            {content}
                        </Row>
                    ) : this.getReorderingRow(content, virtualizeHorizontalData)}
                </Table>
            </MainHeader>
        );
    };

    getResizeHandlers = <T>({columns, uniqueIndex, resizable = true}: {
        columns: Array<Column<T>>,
        uniqueIndex: number,
        resizable?: boolean,
    }): $Shape<{|
        resizable: boolean,
        onStartResize: (column: Column<T>, key: number) => void,
        onResize: (state: DataTableResizeState, column: Column<T>, key: number) => void,
        onStopResize: (state: DataTableResizeState, column: Column<T>, key: number) => void,
    |}> => {
        if (!resizable) {
            return {};
        }

        return {
            resizable,
            onStartResize: () => {
                this.resize.onStartResize(columns[uniqueIndex], uniqueIndex);
            },
            onResize: state => {
                this.resize.onResize(state, columns[uniqueIndex], uniqueIndex);
            },
            onStopResize: (state, resizedColumn) => {
                this.resize.onStopResize(state, columns[uniqueIndex], uniqueIndex);
                if (this.props.onStopResize) {
                    this.props.onStopResize({...resizedColumn, width: state.width});
                }
            }
        };
    };

    renderRowPlaceholder({rowHeight, rowId, rowsVirtualization, rowIndex, fixed}: RowPlaceholderParams) {
        return (rowsVirtualization && (rowIndex < this.state.rowStartIndex || rowIndex > this.state.rowsEndIndex)) ? (
            <DataTableRowPlaceholder
                id={rowId}
                key={rowId}
                mix={classNames({'data-table__row_fixed': fixed})}
                height={rowHeight}
            />
        ) : null;
    }

    renderRows<T, D>(
        {
            indexOffset,
            columns,
            rows,
            fixed,
            pinned,
            virtualizeHorizontalData,
            rowsVirtualization,
            renderRows
        }: RenderRowsParams<T, D>): Array<VirtualizedRow> {
        return rows.map((row, index) => {
            const rowIndex: number = index + indexOffset;
            const {setRowHover, removeRowHover, setCellHover, removeCellHover} = this.hover;
            const {disabledHover} = this.props;
            const rowIdNumber = row.id ?? rowIndex;
            const rowId = generateRowId(this.id, {fixed, pinned}, row.id ?? `${rowIdNumber}`);

            const renderPlaceholder = this.renderRowPlaceholder({
                rowHeight: Number(row.rowHeight ?? DataTableRowSize[TableSize.DEFAULT]),
                rowId,
                rowsVirtualization,
                rowIndex,
                fixed
            });

            if (renderPlaceholder) {
                return renderPlaceholder;
            }

            const preparedData = columns.map((column, columnIndex, array) => {
                const uniqueIndex = virtualizeHorizontalData.cellStartIndex + columnIndex;
                const id = `${this.id}${getPropertyClasses({
                    fixed,
                    pinned
                })}-row-${rowIdNumber}-cell-${uniqueIndex}`;
                const shouldHover = !disabledHover && !row.headerRow;
                const options = {};

                if (shouldHover) {
                    options.setHover = () => setCellHover(uniqueIndex, rowIdNumber, {fixed, pinned});
                    options.removeHover = () => removeCellHover(uniqueIndex, rowIdNumber, {fixed, pinned});
                }

                if (row.headerRow && fixed) {
                    options.column = column;
                }

                const {
                    resizable,
                    onResize,
                    onStartResize,
                    onStopResize
                } = this.getResizeHandlers({
                    columns,
                    uniqueIndex,
                    resizable: Boolean(row.headerRow && fixed)
                });

                const cellOptions = {
                    ...options,
                    isLastChild: isLastChild(array, columnIndex),
                    id,
                    index: uniqueIndex,
                    key: `${uniqueIndex}-${column.columnId}`,
                    headerCell: Boolean(row.headerRow),
                    resizable,
                    onResize,
                    onStartResize,
                    onStopResize,
                    allowedDragArea: !column.fixed
                };

                const rowsParams = {
                    column,
                    row,
                    rowIndex,
                    pinned,
                    isLastChild: isLastChild(columns, columnIndex),
                    fixedRow: fixed
                };

                return renderRows(rowsParams, cellOptions);
            });

            const rowProps = getRowHover({
                fixed,
                pinned,
                disabledHover: Boolean(disabledHover || row.headerRow),
                id: rowIdNumber,
                setRowHover,
                removeRowHover
            });

            return (
                <Row
                    {...rowProps}
                    // {...row.rowHeight ? {rowHeight: Number(row.rowHeight)} : {}}
                    setRowHeight={this.setRowHeight}
                    fixed={fixed}
                    rowSize={this.props.tableSize}
                    index={rowIndex}
                    id={rowId}
                    mainRow={!!row.mainRow}
                    // $FlowFixMe TODO: flow v0.106.0 fix for Row component
                    highlightType={row?.highlightType || HighlightType.NONE}
                    virtualizeHorizontalData={virtualizeHorizontalData}
                    rowHeight={row.rowHeight ? Number(row.rowHeight) : undefined}
                    key={rowIndex}>
                    {preparedData}
                </Row>
            );
        });
    }

    renderTreeRows = <T, D>(
        {
            columns,
            row,
            rowIndex,
            fixed,
            treeDataObj,
            pinned = false,
            virtualizeHorizontalData,
            rowsVirtualization,
            renderTreeRows
        }: RenderTreeRowsParams<T, D>) => {

        const {setRowHover, removeRowHover, setCellHover, removeCellHover} = this.hover;
        const {disabledHover} = this.props;
        const rowIdNumber = row.id ?? rowIndex;
        const rowId = generateRowId(this.id, {fixed, pinned}, row.id ?? `${rowIdNumber}`);

        const renderPlaceholder = this.renderRowPlaceholder({
            rowHeight: Number(row.rowHeight ?? DataTableRowSize[TableSize.DEFAULT]),
            rowId,
            rowsVirtualization,
            rowIndex,
            fixed
        });

        if (renderPlaceholder) {
            return renderPlaceholder;
        }

        const preparedData = columns.map((column, index, array) => {
            const uniqueIndex = virtualizeHorizontalData.cellStartIndex + index;
            const id = `${this.id}${getPropertyClasses({
                fixed,
                pinned
            })}-row-${rowIdNumber}-cell-${uniqueIndex}`;
            const shouldHover = !disabledHover && !row.headerRow;
            const options = {};

            if (shouldHover) {
                options.setHover = () => setCellHover(uniqueIndex, rowIdNumber, {fixed, pinned});
                options.removeHover = () => removeCellHover(uniqueIndex, rowIdNumber, {fixed, pinned});
            }

            if (row.headerRow && fixed) {
                options.column = column;
            }

            const {
                resizable,
                onResize,
                onStartResize,
                onStopResize
            } = this.getResizeHandlers({
                columns,
                uniqueIndex,
                resizable: Boolean(row.headerRow && fixed)
            });

            const cellOptions = {
                ...options,
                treeDataObj,
                isLastChild: isLastChild(array, index),
                id,
                index: uniqueIndex,
                key: `${uniqueIndex}-${column.columnId}`,
                headerCell: Boolean(row.headerRow),
                resizable,
                onResize,
                onStartResize,
                onStopResize
            };

            const rowsParams = {
                column,
                row,
                rowIndex,
                pinned,
                isLastChild: isLastChild(columns, index),
                fixedRow: fixed
            };

            return renderTreeRows(rowsParams, cellOptions);
        });

        const rowProps = getRowHover({
            fixed,
            pinned,
            disabledHover: Boolean(disabledHover || row.headerRow),
            id: rowIdNumber,
            setRowHover,
            removeRowHover
        });

        return (
            <Row
                {...rowProps}
                // {...row.rowHeight ? {rowHeight: Number(row.rowHeight)} : {}}
                setRowHeight={this.setRowHeight}
                fixed={fixed}
                rowSize={this.props.tableSize}
                index={rowIndex}
                id={rowId}
                mainRow={!!row.mainRow}
                highlightType={((row.highlightType: any): $Keys<typeof HighlightType>) || HighlightType.NONE}
                mainTotal={!!row.mainTotal}
                mainRowCollapsed={row.mainRow ? isRowCollapsed(treeDataObj) : false}
                virtualizeHorizontalData={virtualizeHorizontalData}
                rowHeight={row.rowHeight ? Number(row.rowHeight) : undefined}
                key={rowIndex}>
                {preparedData}
            </Row>
        );
    };

    renderTreeRowsStructure = <T = {||}, D = {||}>(
        {
            columns,
            rows,
            fixed,
            virtualizeHorizontalData,
            rowsVirtualization,
            renderTreeRows
        }: RenderTreeRowsStructureParams<T, D>): Array<Element<typeof TBody>> => {

        const generator = indexGenerator(this.flatTreeRows.length);

        return rows.map(section => {
            const topRow = section[0];
            return (
                <TBody key={topRow.id} mix={treeRowsBodyName}>
                    {section.map(row => {
                        return this.renderTreeRows({
                            columns,
                            row,
                            rowIndex: generator.next().value || 0,
                            fixed,
                            virtualizeHorizontalData,
                            treeDataObj: prepareTreeObj(row, topRow),
                            rowsVirtualization,
                            renderTreeRows
                        });
                    })}
                </TBody>
            );
        });
    };

    getTable = <T>(
        {columns, width, fixed = false, virtualizeHorizontalData}: RenderTableType<T>,
        position: $Values<typeof TABLE_PARTS> = TABLE_PARTS.left
    ) => {
        const {data: {rows = [], treeRows = []}, tableSize} = this.props;

        if (!rows.length && !treeRows.length) {
            return null;
        }

        const {topTableRows, ordinaryRows, pinnedRows} = this.splitRows(rows);

        const rowsVirtualization = this.rowsVirtualization;

        const pinnedRowsHeaderMix = position === TABLE_PARTS.left
            ? tableHeaderModificators.leftHeaders
            : tableHeaderModificators.rightHeaders;

        const topTableBodyClasses = classNames({
            'data-table__tbody_zebra-reverse': pinnedRows.length && pinnedRows.length % 2
        });

        const ordinaryTableBodyClasses = classNames({
            'data-table__tbody_zebra-reverse': !(treeRows && treeRows.length)
                && (pinnedRows.length + topTableRows.length) % 2
        });

        return (
            <Fragment>
                {!!pinnedRows.length && (
                    <PinnedRowsHeader
                        className={`${tableHeaderModificators.pinnedHeaders} ${pinnedRowsHeaderMix}`}
                        scrollableRef={this.props.scrollableRef}
                        getHeaderTopOffset={this.props.getHeaderTopOffset}
                        stickyReferenceElement={this.props.stickyReferenceElement}
                        containerEventListenersType={this.props.containerEventListenersType}
                        stickyScroll={position !== TABLE_PARTS.left}
                        disabledScroll={this.props.disabledScroll}
                        wrapperElement={this.props.wrapperElement}>
                        {this.renderTable({
                            columns,
                            width,
                            treeRows,
                            rows: pinnedRows,
                            tableSize: tableSize || TableSize.DEFAULT,
                            fixed,
                            pinned: true,
                            rowsVirtualization: false,
                            virtualizeHorizontalData,
                            indexOffset: 0
                        })}
                    </PinnedRowsHeader>
                )}
                {!!topTableRows.length && (
                    this.renderTable({
                        tableBodyMix: topTableBodyClasses,
                        columns,
                        width,
                        rows: topTableRows,
                        tableSize: tableSize || TableSize.DEFAULT,
                        fixed,
                        topTable: true,
                        rowsVirtualization,
                        virtualizeHorizontalData,
                        indexOffset: pinnedRows.length
                    })
                )}
                {!!(ordinaryRows.length || treeRows && treeRows.length) && (
                    <Fragment>
                        {this.renderTable({
                            tableBodyMix: ordinaryTableBodyClasses,
                            columns,
                            width,
                            treeRows,
                            rows: ordinaryRows,
                            tableSize: tableSize || TableSize.DEFAULT,
                            fixed,
                            rowsVirtualization,
                            virtualizeHorizontalData,
                            indexOffset: pinnedRows.length + topTableRows.length
                        })}
                    </Fragment>
                )}
            </Fragment>
        );
    };

    renderTable = <T, D>(props: TableRowsProps<T, D>) => {
        const {
            columns,
            width,
            rows = [],
            tableSize,
            fixed = false,
            pinned = false,
            topTable = false,
            virtualizeHorizontalData,
            rowsVirtualization,
            indexOffset,
            tableBodyMix
        } = props;

        const virtualizedCellsData = this.getVirtualizedCells<D>(columns, virtualizeHorizontalData);
        const renderRows = ((this.props.renderRows: any): RenderRows<T, D>);
        const renderTreeRows = ((this.props.renderRowsTree: any): RenderRowsTree<DataRow, D>);

        return (
            <Table
                width={width}
                id={`${this.id}${getPropertyClasses({fixed, pinned, topTable})}-table`}
                border={topTable}
                tableSize={tableSize}>
                <THead>
                    <Row virtualizeHorizontalData={virtualizeHorizontalData} rowHeight={0}>
                        {
                            virtualizedCellsData.map((column, key) => {
                                const uniqueIndex = virtualizeHorizontalData.isVirtualizedCells ?
                                    virtualizeHorizontalData.cellStartIndex + key : key;
                                return (
                                    <Cell
                                        id={`${this.id}${getPropertyClasses({
                                            fixed,
                                            pinned,
                                            topTable
                                        })}-header-cell-${uniqueIndex}`}
                                        index={uniqueIndex}
                                        key={`${uniqueIndex}-${column.columnId}`}
                                        column={column}
                                        tableId={`${this.id}`}
                                    />
                                );
                            })
                        }
                    </Row>
                </THead>

                {rows.length && this.props.renderRows ? (
                    <TBody mix={tableBodyMix}>
                        {this.renderRows<T, D>({
                            indexOffset,
                            columns: virtualizedCellsData,
                            rows,
                            fixed,
                            pinned,
                            virtualizeHorizontalData,
                            rowsVirtualization,
                            renderRows
                        })}
                    </TBody>
                ) : null}

                {this.preparedTreesSection.length && this.props.renderRowsTree ?
                    this.renderTreeRowsStructure({
                        columns: virtualizedCellsData,
                        fixed,
                        rows: this.preparedTreesSection,
                        virtualizeHorizontalData,
                        rowsVirtualization,
                        renderTreeRows
                    }) : null}
            </Table>
        );
    };

    getElement = ({fixed, pinned, index}: PropertyElement) => {
        return document.getElementById(
            `${this.id}${getPropertyClasses({fixed, pinned})}-row-${index}`
        );
    };

    setRowHeight = (index: number, height: number, targetRow: ?HTMLDivElement) => {
        const fixedRow = this.getElement({fixed: true, index});
        const row = this.getElement({index});

        const fixedRowPinned = this.getElement({fixed: true, pinned: true, index});
        const rowPinned = this.getElement({pinned: true, index});

        if (fixedRowPinned && rowPinned && rowPinned === targetRow) {
            fixedRowPinned.style.setProperty('--row-height', convertPxToRem(height));
            rowPinned.style.setProperty('--row-height', convertPxToRem(height));
        } else if (fixedRow && row && row === targetRow) {
            fixedRow.style.setProperty('--row-height', convertPxToRem(height));
            row.style.setProperty('--row-height', convertPxToRem(height));
        }
    };

    getScrollHandler = () => this.externalScrollHandler;
    getScroll = () => this.scroll;

    getScrollableArea = () => {
        const {renderCustomArrows, onScrollableSizeChange, overscanCellsRatio, data} = this.props;
        const {preparedColumns, fixedColumnsWidth, otherColumnsWidth} = this.state;

        const leftPartRenderData = {
            columns: preparedColumns.fixed,
            width: fixedColumnsWidth,
            fixed: true,
            virtualizeHorizontalData: DefaultVirtualizeHorizontalData
        };

        const rightPartRenderData = {
            columns: preparedColumns.others,
            width: otherColumnsWidth.width,
            offsetWidth: otherColumnsWidth.offsetWidth,
            widthArray: otherColumnsWidth.widthArray
        };
        const {onDrop, onDragOver, onDragEnter} = this.context;

        return (
            <ScrollableArea>
                <Left
                    id={`${this.id}-left`}
                    data-id={ALLOWED_DRAG_AND_DROP_AREA_DATA_ID}
                    {...{onDrop, onDragOver, onDragEnter}}>
                    {this.renderHeaders<DataColumn>(leftPartRenderData)}
                    {this.getTable(leftPartRenderData)}
                </Left>

                <Right
                    id={`${this.id}-right`}
                    className={hashedClassnames['data-table__scroll']}>
                    <VirtualizedCellsArea
                        disabledScroll={!!this.props.disabledScroll}
                        columnsData={rightPartRenderData}
                        overscanCellsRatio={overscanCellsRatio}
                        isVirtualizedCells={this.isCellVirtualizationEnable(data.columns.length)}
                        onScrollableSizeChange={onScrollableSizeChange}
                        renderCustomArrows={renderCustomArrows}
                        getTable={this.getTable}
                        renderHeaders={this.renderHeaders}
                        setScrollablePositionRatio={this.props.setScrollablePosition}
                        wrapperElement={this.props.wrapperElement}
                    />
                </Right>
                <div
                    data-id={ALLOWED_DRAG_AND_DROP_AREA_DATA_ID}
                    {...{
                    onDrop,
                    onDragOver,
                    onDragEnter,
                    'style': {
                        flex: '1 1',
                    },
                }}/>
            </ScrollableArea>
        );
    };

    isEmptyData = () => {
        const {data: {rows = [], treeRows = []}} = this.props;
        return !rows.length && !treeRows.length;
    };

    getCustomNavigation = () => {
        const {
            paginationStripe,
            customNavigation
        } = this.props;
        return customNavigation ??
            (<DataTableDefaultNavigation stripe={paginationStripe} otherColumnsWidth={this.state.otherColumnsWidth}/>);
    };

    getNavigation = () => {
        const {showNoDataMsg} = this.props;
        const empty = showNoDataMsg ? !showNoDataMsg : this.isEmptyData();

        return <Navigation empty={empty}>{this.getCustomNavigation}</Navigation>;
    };

    render() {
        const {
            mix,
            showNoDataMsg,
            tableMessage
        } = this.props;

        return (
            <div
                id={this.id}
                className={classNames('data-table', mix && {[mix]: mix !== ''})}
                ref={el => {
                    this.dataTable = el;
                    if (this.props.onRef) {
                        this.props.onRef(el);
                    }
                }}>

                {this.getScrollableArea()}

                {showNoDataMsg && tableMessage ? <div className="data-table__message">{tableMessage}</div> : null}

                {this.getNavigation()}
            </div>
        );
    }
}

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./data-table-hover");

require("./typedef");

require("./constants");

require("./data-table-cell");

require("./contexts/data-table-drag-events-context");

require("./data-table-resize-class");

require("./data-table-tbody");
